import styled, { keyframes } from 'styled-components';
import { semanticTheme } from '@tools/ui/dime';

const CLOSE_ANIMATION_MS = 300;
const CARD_STAGGER_MS = 120;

const slideIn = keyframes`
  from {
    transform: translateX(calc(100% + ${semanticTheme.dime.spacing.xl}));
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(calc(100% + ${semanticTheme.dime.spacing.xl}));
    opacity: 0;
  }
`;

const fadeInUp = keyframes`
  from {
    transform: translateY(${semanticTheme.dime.spacing['2m']});
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

export const PanelWrapper = styled.div<{ $isClosing: boolean; $skipEnter: boolean }>`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 480px;
  margin: ${semanticTheme.dime.spacing.xl};
  display: flex;
  flex-direction: column;
  background: ${semanticTheme.dime.colors.fill.primary};
  border-radius: ${semanticTheme.dime.radius.l};
  box-shadow: 0 0 14px 0 rgba(0, 0, 0, 0.08);
  z-index: 1001;
  overflow: hidden;
  animation: ${({ $isClosing, $skipEnter }) => {
      if ($isClosing) {
        return slideOut;
      }
      return $skipEnter ? 'none' : slideIn;
    }}
    ${({ $isClosing }) => ($isClosing ? CLOSE_ANIMATION_MS : 350)}ms
    cubic-bezier(0.4, 0, 0.2, 1) forwards;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${semanticTheme.dime.spacing.l} ${semanticTheme.dime.spacing.xl};
  border-bottom: 1px solid ${semanticTheme.dime.colors.border.static};
  background: ${semanticTheme.dime.colors.fill.primary};
  flex-shrink: 0;
  border-radius: ${semanticTheme.dime.radius.m} ${semanticTheme.dime.radius.m} 0 0;
`;

export const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${semanticTheme.dime.spacing.xl};
  background: ${semanticTheme.dime.colors.background.secondary};
  display: flex;
  flex-direction: column;
  gap: ${semanticTheme.dime.spacing.xl};
`;

export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${semanticTheme.dime.spacing['2m']};
  padding: ${semanticTheme.dime.spacing.l};
  background: ${semanticTheme.dime.colors.fill.primary};
  border-top: 1px solid ${semanticTheme.dime.colors.border.static};
  flex-shrink: 0;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.06);
  border-radius: 0 0 ${semanticTheme.dime.radius.l} ${semanticTheme.dime.radius.l};
`;

export const FooterTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const AnimatedCard = styled.div<{
  $delay: number;
  $visible: boolean;
  $skipAnim: boolean;
}>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  animation: ${({ $visible, $skipAnim }) =>
      $skipAnim || !$visible ? 'none' : fadeInUp}
    400ms cubic-bezier(0.4, 0, 0.2, 1) both;
  animation-delay: ${({ $delay }) => $delay * CARD_STAGGER_MS}ms;
`;

export const AiPanelCard = styled.div<{ $gap?: number; $transparent?: boolean }>`
  background: ${({ $transparent }) => ($transparent ? 'transparent' : semanticTheme.dime.colors.fill.primary)};
  border: ${({ $transparent }) => ($transparent ? 'none' : `1px solid ${semanticTheme.dime.colors.border.static}`)};
  border-radius: ${semanticTheme.dime.radius.m};
  padding: ${({ $transparent }) => ($transparent ? '0' : semanticTheme.dime.spacing.l)};
  margin-left: ${({ $transparent }) => ($transparent ? semanticTheme.dime.spacing.l : '0')};
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap != null ? `${$gap}px` : semanticTheme.dime.spacing['2m']};
`;
